import { BoxProps, VStack } from '@chakra-ui/react'
import {
  Assessment,
  ASSESSMENTS,
  FieldMap,
  FieldMapValue,
  getNestedUserFields,
  makeAllFieldsAdmin,
  ShareCorrections,
  UpdateCallback,
} from '@hb/shared'

import { doc, DocumentReference, updateDoc } from 'firebase/firestore'
import { set as nestedSet } from 'nested-property'
import { useCallback, useContext, useMemo } from 'react'
import { ASSESSMENTS_REF } from '../../../collections/firestoreCollections'
import { useApp } from '../../../contexts/AppContext'
// import { AssessmentInterfaceContext } from '../../contexts/AssessmentContext'
import { PopUpMessageContext } from '../../../contexts/PopUpMessage/PopUpMessageContext'
import { updateAssessmentAnswers } from '../../../hooks/backend/assessments'

import { useProfile } from '../../../contexts'
import { DataView } from '../../DataView'

const sectionBoxProps: BoxProps = {
  borderBottom: '1px solid #cdcdcd',
  bg: 'white',
  borderRadius: 4,
  boxShadow: 'md',
}
const QuestionnaireSectionView = ({
  id,
  baseStoragePath,
  fields,
  assessmentRef,
  handleSave,
}: {
  id: 'delivery' | 'insuranceInfo'
  baseStoragePath: string
  fields: FieldMap
  assessmentRef: DocumentReference<Assessment>
  handleSave: (id: 'delivery' | 'insuranceInfo', updated: FieldMapValue) => Promise<UpdateCallback>
}) => {
  const { appName } = useApp()
  const {
    selectedAssessment: { populated: assessment },
    pregnancyCorrections,
    pregnancyData,
  } = useProfile()

  const data = useMemo(() => {
    if (id === 'delivery') return pregnancyData?.delivery
    if (id === 'insuranceInfo') return pregnancyData?.insuranceInfo
    return null
  }, [id, pregnancyData])

  const corrections = useMemo(() => {
    if (id === 'delivery') return pregnancyCorrections?.delivery
    if (id === 'insuranceInfo') return pregnancyCorrections?.insuranceInfo
    return null
  }, [id, pregnancyCorrections])

  const storagePath = useMemo(() => `${baseStoragePath}/${id}`, [baseStoragePath, id])

  const handleShareCorrections = useCallback(
    async (path: string | null, shared: ShareCorrections | null) =>
      updateDoc(
        assessmentRef,
        `corrections.${id}.${path ? `${path}.` : ''}correctionsShared`,
        shared,
      ),
    [assessmentRef, id],
  )

  const onShareCorrections = useMemo(
    () => (appName === 'app' ? handleShareCorrections : undefined),
    [appName, handleShareCorrections],
  )

  const handleSubmit = useCallback(
    (updated: FieldMapValue) => handleSave(id, updated),
    [handleSave, id],
  )

  const canSubmit = useMemo(() => {
    if (appName === 'app') return true
    return id === 'delivery' || !assessment?.submittedOn
  }, [appName, assessment, id])

  const onSubmit = useMemo(() => (canSubmit ? handleSubmit : undefined), [canSubmit, handleSubmit])

  const handleUpdateField = useCallback(
    (path: string, value: FieldMapValue) => {
      const updated = { ...corrections }
      nestedSet(updated, path, value)
      return handleSave(id, updated)
    },
    [handleSave, id, corrections],
  )

  const onUpdateField = useMemo(
    () => (canSubmit ? handleUpdateField : undefined),
    [canSubmit, handleUpdateField],
  )

  return (
    <DataView
      withEditModal={!!onSubmit}
      isCorrecting={!!assessment?.data}
      baseStoragePath={storagePath}
      boxProps={sectionBoxProps}
      field={fields}
      alwaysExpanded
      onShareCorrections={onShareCorrections}
      adminView
      data={data}
      corrections={corrections}
      onSubmit={onSubmit}
      updateField={onUpdateField}
    />
  )
}

export const AssessmentQuestionnaireView = ({ assessmentId }: { assessmentId: string }) => {
  const {
    selectedAssessment: { populated: assessment },
  } = useProfile()
  const { appName } = useApp()
  const fields = useMemo(
    () => makeAllFieldsAdmin(getNestedUserFields(true, assessment)),
    [assessment],
  )

  const deliveryField = useMemo(() => fields.children.delivery, [fields])
  const insuranceInfoField = useMemo(() => fields.children.insuranceInfo, [fields])

  const { processResponse } = useContext(PopUpMessageContext)
  const baseStoragePath = useMemo(
    () => `${ASSESSMENTS}/${assessmentId}/corrections`,
    [assessmentId],
  )

  const handleSave = useCallback(
    async (id: 'delivery' | 'insuranceInfo', updated: FieldMapValue) =>
      updateAssessmentAnswers({
        id: assessmentId,
        appName,
        data: { [id]: updated },
      })
        .then(() => processResponse({ success: 'Updated!' }))
        .catch(err => processResponse({ error: err.message })),
    [assessmentId, appName, processResponse],
  )
  const assessmentRef = useMemo(() => doc(ASSESSMENTS_REF, assessmentId), [assessmentId])
  return (
    <VStack w="100%" spacing={2}>
      {deliveryField ? (
        <QuestionnaireSectionView
          id="delivery"
          baseStoragePath={baseStoragePath}
          fields={deliveryField as FieldMap}
          assessmentRef={assessmentRef}
          handleSave={handleSave}
        />
      ) : null}
      {insuranceInfoField ? (
        <QuestionnaireSectionView
          id="insuranceInfo"
          baseStoragePath={baseStoragePath}
          fields={insuranceInfoField as FieldMap}
          assessmentRef={assessmentRef}
          handleSave={handleSave}
        />
      ) : null}
    </VStack>
  )
}
